import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import { usePusherAppReload } from 'src/hooks/use-pusher-app-reload';
import { useShowWebStudioEditor } from 'src/enterprise-admin/hooks/use-show-web-studio-editor';
import RedirectUser from 'src/routes/redirect-user';
import { SuperAdminIndex } from 'src/super-admin';
import ChainAdmin from 'src/chain-admin';
import DispensaryAdmin from 'src/dispensary-admin';
import EnterpriseAdmin from 'src/enterprise-admin';
import {
  LEGACY_DIGITAL_SIGNAGE_PATHS,
  LegacyDigitalSignageRedirect,
} from 'src/enterprise-admin/digital-signage/legacy-redirect';
import WebStudioEditor from 'src/enterprise-admin/web-studio/editor';
import ExportRedirect from 'src/routes/export-redirect/index';
import ZendeskLogin from 'src/routes/zendesk-login';
import useAdminChatWidget from './hooks/use-admin-chat-widget';

// https://github.com/airbnb/react-with-styles/issues/137
import 'react-dates/initialize';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-dates/lib/css/_datepicker.css';

export default function App(): JSX.Element {
  useAdminChatWidget();
  usePusherAppReload();
  // Mirrors the same flag check that gates the Web Studio nav link/route in
  // useEnterpriseAdminLinks (shared via useShowWebStudioEditor). This editor route
  // is registered outside that links loop (see comment below), so it needs its own
  // check -- otherwise a bookmarked or shared deep link would keep reaching the
  // full editor shell after the flag is turned off, even though data operations
  // inside it would then fail on auth.
  const showWebStudio = useShowWebStudioEditor();

  return (
    <Switch>
      <Route path='/exports/:exportId' component={ExportRedirect} />
      <Route path='/zendesk' component={ZendeskLogin} />

      {/* Registered above /enterprise/:enterpriseId so it renders full-screen, without the AdminLayout sidebar/header. */}
      <Route
        path='/enterprise/:enterpriseId/web-studio/editor/:modelId'
        render={(routeProps): JSX.Element => {
          if (!showWebStudio) {
            // Same fallback the links loop uses when a flagged-off link is hit directly:
            // redirect into the enterprise's default landing page rather than 404.
            return <Redirect to={`/enterprise/${routeProps.match.params.enterpriseId}/orders`} />;
          }
          // WebStudioEditor takes no props of its own -- it reads enterpriseId/modelId
          // via useParams -- so routeProps isn't spread onto it.
          return <WebStudioEditor />;
        }}
      />

      <Route path='/super' component={SuperAdminIndex} />
      <Route path='/chains/:chainId' component={ChainAdmin} />
      <Route path='/dispensaries/:id' component={DispensaryAdmin} />

      {/* Renamed to /digital-signage (ECOMM-13331) -- registered above the catch-all
          /enterprise/:enterpriseId route so old bookmarked/shared links (both the original
          /menu-boards path and the briefly-named /digital-displays path) still land on the
          right page instead of falling through to EnterpriseAdmin, which no longer has a
          route mounted at either old prefix. */}
      <Route path={LEGACY_DIGITAL_SIGNAGE_PATHS} component={LegacyDigitalSignageRedirect} />
      <Route path='/enterprise/:enterpriseId' component={EnterpriseAdmin} />

      <Route path='/*' component={RedirectUser} />
    </Switch>
  );
}
